import React, { useRef } from 'react';
import {
  Box, Grid, Card, Avatar, Typography, IconButton, Tooltip,
  Stack, LinearProgress, Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import generatePDF from 'react-to-pdf';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BuildIcon from '@mui/icons-material/Build';
import PaymentsIcon from '@mui/icons-material/Payments';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import SettingsIcon from '@mui/icons-material/Settings';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { PRIMARY } from 'themes/colors';

interface IAgent {
  agent: string;
  agent_photo: string;
  totalConnection: number;
  load: number;
  installationComplete: number;
  regulator: number;
  pipe: number;
  paidLight: number;
  nonHpOven: number;
  hpOven: number;
  bplOven: number;
  paidAmount: number;
  remarks: string;
}

interface IPricing {
  hpOvenPricing: number;
  nonHpOvenPricing: number;
}

interface ConnectionDetailsProps {
  agent: IAgent;
  pricing: IPricing;
  getRole: () => string;
  setOpen: (value: boolean) => void;
}

const MetricRow = ({
  label, value, total, color,
}: {
  label: string; value: number; total: number; color: string;
}) => {
  const pct = total > 0 ? Math.min((value / total) * 100, 100) : 0;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" fontWeight={700} sx={{ color }}>
          {Math.round(pct)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6, borderRadius: 3,
          bgcolor: alpha(color, 0.12),
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
        }}
      />
    </Box>
  );
};

const DualMetric = ({
  leftVal, leftLabel, rightVal, rightLabel, rightColor,
}: {
  leftVal: number; leftLabel: string; rightVal: number; rightLabel: string; rightColor?: string;
}) => (
  <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.5 }}>
    <Box>
      <Typography variant="h5" fontWeight={700} lineHeight={1.2}>{leftVal}</Typography>
      <Typography variant="caption" color="text.secondary">{leftLabel}</Typography>
    </Box>
    <Box sx={{ textAlign: 'right' }}>
      <Typography variant="h5" fontWeight={700} lineHeight={1.2} color={rightColor || 'error.main'}>
        {rightVal}
      </Typography>
      <Typography variant="caption" color="text.secondary">{rightLabel}</Typography>
    </Box>
  </Stack>
);

const StatCard = ({
  icon, iconBg, title, children,
}: {
  icon: React.ReactNode; iconBg: string; title: string; children: React.ReactNode;
}) => (
  <Card
    elevation={0}
    sx={{
      height: '100%', borderRadius: 3,
      border: '1px solid', borderColor: 'grey.200',
      p: 2.5,
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box
        sx={{
          width: 40, height: 40, borderRadius: 2,
          bgcolor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
    </Box>
    <Stack spacing={1.5}>{children}</Stack>
  </Card>
);

export const ConnectionDetails: React.FC<ConnectionDetailsProps> = ({ agent, pricing, getRole, setOpen }) => {
  const pdfRef = useRef<HTMLDivElement>(null);
  const totalPayable = (agent.nonHpOven * pricing.nonHpOvenPricing) + (agent.hpOven * pricing.hpOvenPricing);
  const paidPercent = totalPayable > 0 ? Math.round((agent.paidAmount / totalPayable) * 100) : 0;

  return (
    <Box id="pdfId" ref={pdfRef}>
      {/* Hero Agent Card */}
      <Card
        elevation={0}
        sx={{
          mb: 3, borderRadius: 3,
          background: `linear-gradient(135deg, ${PRIMARY.darker} 0%, ${PRIMARY.main} 60%, #5bc88f 100%)`,
          color: 'white', overflow: 'hidden', position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -60, right: 80, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />
        <Box sx={{ p: { xs: 2.5, sm: 3 }, position: 'relative', zIndex: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
            <Avatar
              src={agent.agent_photo}
              sx={{
                width: { xs: 72, sm: 90 }, height: { xs: 72, sm: 90 },
                border: '3px solid rgba(255,255,255,0.3)',
                fontSize: 32, bgcolor: 'rgba(255,255,255,0.2)',
                flexShrink: 0,
              }}
            >
              {agent.agent?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                {agent.agent}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={`${agent.totalConnection} Total`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                />
                <Chip
                  label={`${agent.load} Paid`}
                  size="small"
                  sx={{ bgcolor: 'rgba(76,175,80,0.4)', color: 'white', fontWeight: 600 }}
                />
                {agent.totalConnection - agent.load > 0 && (
                  <Chip
                    label={`${agent.totalConnection - agent.load} Pending`}
                    size="small"
                    sx={{ bgcolor: 'rgba(244,67,54,0.4)', color: 'white', fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
            {getRole() === 'superadmin' && (
              <Tooltip title="Edit Details">
                <IconButton
                  onClick={() => setOpen(true)}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)', color: 'white', flexShrink: 0,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      </Card>

      {/* Stats Grid */}
      <Grid container spacing={2.5}>
        {/* Connection Status */}
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            icon={<AccountBalanceIcon sx={{ color: '#1565c0', fontSize: 20 }} />}
            iconBg={alpha('#1565c0', 0.1)}
            title="Connection Status"
          >
            <MetricRow label="Load paid vs total" value={agent.load} total={agent.totalConnection || 1} color="#1565c0" />
            <DualMetric leftVal={agent.load} leftLabel="Paid" rightVal={agent.totalConnection - agent.load} rightLabel="Pending" />
          </StatCard>
        </Grid>

        {/* Installation Progress */}
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            icon={<BuildIcon sx={{ color: '#e65100', fontSize: 20 }} />}
            iconBg={alpha('#e65100', 0.1)}
            title="Installation Progress"
          >
            <MetricRow label="Completed vs load paid" value={agent.installationComplete} total={agent.load || 1} color="#e65100" />
            <DualMetric leftVal={agent.installationComplete} leftLabel="Complete" rightVal={agent.load - agent.installationComplete} rightLabel="Pending" />
          </StatCard>
        </Grid>

        {/* Oven Distribution */}
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            icon={<LocalFireDepartmentIcon sx={{ color: '#bf360c', fontSize: 20 }} />}
            iconBg={alpha('#bf360c', 0.1)}
            title="Oven Distribution"
          >
            <MetricRow label="HP Oven" value={agent.hpOven} total={agent.totalConnection || 1} color="#bf360c" />
            <MetricRow label="Non-HP Oven" value={agent.nonHpOven} total={agent.totalConnection || 1} color="#f57c00" />
            <MetricRow label="BPL Oven" value={agent.bplOven} total={agent.totalConnection || 1} color="#6d4c41" />
          </StatCard>
        </Grid>

        {/* Light Distribution */}
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            icon={<ElectricBoltIcon sx={{ color: '#f9a825', fontSize: 20 }} />}
            iconBg={alpha('#f9a825', 0.1)}
            title="Light Distribution"
          >
            <MetricRow
              label="Paid vs eligible"
              value={agent.paidLight}
              total={(agent.hpOven + agent.nonHpOven) || 1}
              color="#f9a825"
            />
            <DualMetric
              leftVal={agent.paidLight}
              leftLabel="Paid"
              rightVal={agent.hpOven + agent.nonHpOven - agent.paidLight}
              rightLabel="Due"
            />
          </StatCard>
        </Grid>

        {/* Regulator Distribution */}
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            icon={<SettingsIcon sx={{ color: '#6a1b9a', fontSize: 20 }} />}
            iconBg={alpha('#6a1b9a', 0.1)}
            title="Regulator Distribution"
          >
            <MetricRow label="Paid vs load" value={agent.regulator} total={agent.load || 1} color="#6a1b9a" />
            <DualMetric leftVal={agent.regulator} leftLabel="Paid" rightVal={agent.load - agent.regulator} rightLabel="Due" />
          </StatCard>
        </Grid>

        {/* Pipe Distribution */}
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            icon={<PlumbingIcon sx={{ color: '#00695c', fontSize: 20 }} />}
            iconBg={alpha('#00695c', 0.1)}
            title="Pipe Distribution"
          >
            <MetricRow label="Paid vs load" value={agent.pipe} total={agent.load || 1} color="#00695c" />
            <DualMetric leftVal={agent.pipe} leftLabel="Paid" rightVal={agent.load - agent.pipe} rightLabel="Due" />
          </StatCard>
        </Grid>

        {/* Payment Overview — full width */}
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{ borderRadius: 3, border: '1px solid', borderColor: 'grey.200', overflow: 'hidden' }}
          >
            <Box
              sx={{
                px: 2.5, py: 2,
                bgcolor: alpha('#4527a0', 0.04),
                borderBottom: '1px solid', borderColor: 'grey.200',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 40, height: 40, borderRadius: 2,
                  bgcolor: alpha('#4527a0', 0.1),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <PaymentsIcon sx={{ color: '#4527a0', fontSize: 20 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={700}>Payment Overview</Typography>
            </Box>
            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Total Payable</Typography>
                  <Typography variant="h4" fontWeight={700}>₹{totalPayable.toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Amount Paid</Typography>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    ₹{agent.paidAmount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Amount Due</Typography>
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    ₹{(totalPayable - agent.paidAmount).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(paidPercent, 100)}
                    sx={{
                      height: 10, borderRadius: 5,
                      bgcolor: alpha('#4527a0', 0.1),
                      '& .MuiLinearProgress-bar': { bgcolor: '#4527a0', borderRadius: 5 },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {paidPercent}% collected
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
